/**
 * @fileoverview Ferramentas de Super Admin da conexão: resolver conversas em
 * massa e excluir conversas e contatos, com prévia, confirmação forte,
 * progresso e histórico. Só aparece para superadmin — e o backend confere de
 * novo.
 *
 * O estado vem do servidor: ao abrir, a lista traz a operação ativa da
 * conexão. Enquanto o modal está aberto, o progresso chega pelo socket (salas
 * de quem criou, retomou ou pediu o cancelamento) e por consulta periódica —
 * da operação ativa, ou da lista quando não há nenhuma, o que também revela
 * operação iniciada por outro superadmin. Fechar o modal não afeta nada.
 */

import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { Dialog, useMediaQuery, useTheme } from "@material-ui/core";
import { CheckCheck, History, Loader2, MessageSquareX, ShieldAlert, UserX, X } from "lucide-react";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import { SocketContext } from "../../context/Socket/SocketContext";
import useStyles from "./styles";
import CleanupPanel from "./CleanupPanel";
import ConfirmDialog from "./ConfirmDialog";
import ProgressPanel from "./ProgressPanel";
import HistoryPanel from "./HistoryPanel";
import {
	errorCodeOf,
	filtersSummary,
	fmtNumber,
	isActiveStatus,
	messageForCode,
	newest,
	phraseOf,
	t,
} from "./format";

const POLL_ACTIVE_MS = 4000;
const POLL_IDLE_MS = 8000;

// Confirmação recusada porque a prévia da tela ficou para trás: recalcula.
const STALE_PREVIEW_CODES = ["ERR_ADMIN_OPERATION_PREVIEW_EXPIRED", "ERR_NOTHING_TO_DELETE", "ERR_NOTHING_TO_RESOLVE"];

const SECTIONS = [
	{ id: "resolve_tickets", icon: CheckCheck, label: "nav.resolve" },
	{ id: "delete_tickets", icon: MessageSquareX, label: "nav.tickets" },
	{ id: "delete_contacts", icon: UserX, label: "nav.contacts" },
	{ id: "history", icon: History, label: "nav.history" },
];

// Primeira página vinda do servidor, sem perder as páginas já carregadas nem
// voltar um item para um estado mais antigo.
const mergeHistory = (current, fresh) => {
	const byId = new Map(current.map((item) => [item.id, item]));
	fresh.forEach((item) => byId.set(item.id, newest(byId.get(item.id), item)));
	return Array.from(byId.values()).sort((a, b) => b.id - a.id);
};

const ConnectionAdminToolsModal = ({ open, onClose, whatsApp, subtitle }) => {
	const classes = useStyles();
	const theme = useTheme();
	const fullScreen = useMediaQuery(theme.breakpoints.down("xs"));
	const { user } = useContext(AuthContext);
	const socketManager = useContext(SocketContext);

	const whatsAppId = whatsApp?.id;
	const userCompanyId = user?.companyId;
	const base = `/whatsapp/${whatsAppId}/admin-tools`;
	// Resposta que chega depois de trocar de conexão (ou fechar) é descartada.
	const liveBase = useRef(null);
	liveBase.current = open ? base : null;

	const [section, setSection] = useState("resolve_tickets");
	const [loading, setLoading] = useState(false);
	const [history, setHistory] = useState([]);
	const [nextCursor, setNextCursor] = useState(null);
	const [loadingMore, setLoadingMore] = useState(false);
	const [focusOp, setFocusOp] = useState(null);
	const [confirmRequest, setConfirmRequest] = useState(null);
	const [previewNonce, setPreviewNonce] = useState(0);

	// Atualização de uma operação (socket, consulta ou resposta de ação).
	const applyOp = useCallback(
		(op) => {
			if (!op || op.whatsappId !== whatsAppId) return;
			setHistory((prev) => mergeHistory(prev, [op]));
			setFocusOp((prev) => {
				if (!prev) return isActiveStatus(op.status) ? op : prev;
				if (prev.id !== op.id) return isActiveStatus(op.status) && !isActiveStatus(prev.status) ? op : prev;
				return newest(prev, op);
			});
		},
		[whatsAppId]
	);

	// Lista (primeira página + operação ativa), mesclada ao que já está na tela.
	const loadOperations = useCallback(async () => {
		const requested = base;
		const { data } = await api.get(`${requested}/operations`);
		if (liveBase.current !== requested) return null;
		setHistory((prev) => mergeHistory(prev, data.operations || []));
		setFocusOp((prev) => {
			if (data.active) return newest(prev?.id === data.active.id ? prev : null, data.active);
			if (!prev) return null;
			const fresh = (data.operations || []).find((item) => item.id === prev.id);
			return fresh ? newest(prev, fresh) : prev;
		});
		return data;
	}, [base]);

	// Abrir: estado limpo e o que está no servidor.
	useEffect(() => {
		if (!open || !whatsAppId) return undefined;
		let cancelled = false;
		setSection("resolve_tickets");
		setHistory([]);
		setNextCursor(null);
		setFocusOp(null);
		setConfirmRequest(null);
		setLoading(true);
		loadOperations()
			.then((data) => {
				if (cancelled || !data) return;
				// Só a abertura define a paginação; as consultas periódicas só
				// mesclam a primeira página.
				setNextCursor(data.nextCursor || null);
				if (data.active) setSection(data.active.operation);
			})
			.catch((err) => {
				if (!cancelled) toastError(err);
			})
			.finally(() => {
				if (!cancelled) setLoading(false);
			});
		return () => {
			cancelled = true;
		};
	}, [open, whatsAppId, loadOperations]);

	// Progresso pelo socket. O wrapper do socket é descartado ao fechar — o
	// disconnect dele solta os próprios listeners, sem derrubar a conexão.
	useEffect(() => {
		if (!open || !whatsAppId) return undefined;
		const companyId = userCompanyId || localStorage.getItem("companyId");
		if (!companyId) return undefined;
		const socket = socketManager.getSocket(companyId);
		socket.on(`company-${companyId}-adminOperation`, (data) => applyOp(data?.operation));
		return () => socket.disconnect();
	}, [open, whatsAppId, userCompanyId, socketManager, applyOp]);

	// Consulta periódica: da operação ativa (progresso) ou, sem nenhuma, da
	// lista (revela operação de outro superadmin). Pula aba oculta e não
	// sobrepõe consultas.
	const focusId = focusOp?.id;
	const focusActive = !!focusOp && isActiveStatus(focusOp.status);
	useEffect(() => {
		if (!open || !whatsAppId) return undefined;
		let busy = false;
		const tick = async () => {
			if (busy || document.hidden) return;
			busy = true;
			try {
				if (focusActive) {
					const requested = base;
					const { data } = await api.get(`${requested}/operations/${focusId}`);
					if (liveBase.current === requested) applyOp(data);
				} else {
					await loadOperations();
				}
			} catch (err) {
				// próxima rodada tenta de novo
			} finally {
				busy = false;
			}
		};
		const timer = setInterval(tick, focusActive ? POLL_ACTIVE_MS : POLL_IDLE_MS);
		return () => clearInterval(timer);
	}, [open, whatsAppId, focusActive, focusId, base, applyOp, loadOperations]);

	// Cancelamento aberto para uma operação que terminou nesse meio-tempo: some.
	useEffect(() => {
		if (confirmRequest?.mode !== "cancel") return;
		const target = history.find((item) => item.id === confirmRequest.operationId);
		if (target && !isActiveStatus(target.status)) setConfirmRequest(null);
	}, [confirmRequest, history]);

	const handleLoadMore = async () => {
		if (!nextCursor) return;
		const requested = base;
		setLoadingMore(true);
		try {
			const { data } = await api.get(`${requested}/operations`, { params: { cursor: nextCursor } });
			if (liveBase.current !== requested) return;
			setHistory((prev) => mergeHistory(prev, data.operations || []));
			setNextCursor(data.nextCursor || null);
		} catch (err) {
			toastError(err);
		} finally {
			setLoadingMore(false);
		}
	};

	const requestResume = (op) =>
		setConfirmRequest({
			mode: "resume",
			operationId: op.id,
			operation: op.operation,
			phrase: phraseOf(op),
			summary: [
				`${t(`history.operation.${op.operation}`)} — ${filtersSummary(op.operation, op.filters)}`,
				t("confirm.summary.resumeProgress", {
					processed: fmtNumber(op.processedCount),
					total: fmtNumber(op.totalCount),
				}),
			],
		});

	const requestCancel = (op) => setConfirmRequest({ mode: "cancel", operationId: op.id, operation: op.operation });

	// Lança o erro para o diálogo mostrar (senha, frase, limite...).
	const handleConfirmSubmit = async ({ password, confirmationPhrase }) => {
		const req = confirmRequest;
		const requested = base;
		try {
			let response;
			if (req.mode === "cancel") {
				response = await api.post(`${requested}/operations/${req.operationId}/cancel`);
			} else if (req.mode === "resume") {
				response = await api.post(`${requested}/operations/${req.operationId}/resume`, {
					password,
					confirmationPhrase,
				});
			} else {
				response = await api.post(`${requested}/operations`, {
					operation: req.operation,
					filters: req.filters,
					snapshot: req.snapshot,
					password,
					confirmationPhrase,
				});
			}
			if (liveBase.current !== requested) return;
			setConfirmRequest(null);
			if (req.mode !== "cancel") setSection(response.data.operation);
			// Pela mesma regra do socket: numa operação rápida a conclusão chega
			// antes desta resposta ("na fila") e não pode ser sobrescrita por ela.
			applyOp(response.data);
		} catch (err) {
			const code = errorCodeOf(err);
			if (liveBase.current !== requested) return;
			if (req.mode === "create" && STALE_PREVIEW_CODES.includes(code)) {
				// A prévia da tela ficou para trás: recalcula e o superadmin confere de novo.
				setConfirmRequest(null);
				setPreviewNonce((n) => n + 1);
				toast.warn(`${messageForCode(code)} ${t("cleanup.recalculated")}`);
				return;
			}
			// A lista reflete a tentativa negada, a operação já em andamento ou a
			// que ficou "falhou" por fila indisponível.
			if (code) loadOperations().catch(() => undefined);
			throw err;
		}
	};

	if (!whatsApp) return null;

	const activeOp = focusOp && isActiveStatus(focusOp.status) ? focusOp : null;
	const showProgress = section !== "history" && focusOp && (activeOp || focusOp.operation === section);

	const renderContent = () => {
		if (loading) {
			return (
				<div className={classes.centered}>
					<Loader2 size={20} className={classes.spin} />
				</div>
			);
		}
		if (section === "history") {
			return (
				<HistoryPanel
					items={history}
					nextCursor={nextCursor}
					loadingMore={loadingMore}
					canResume={!activeOp}
					onLoadMore={handleLoadMore}
					onResume={requestResume}
				/>
			);
		}
		if (showProgress) {
			return (
				<ProgressPanel op={focusOp} onCancel={requestCancel} onResume={requestResume} onNew={() => setFocusOp(null)} />
			);
		}
		return (
			<CleanupPanel
				key={section}
				operation={section}
				whatsAppId={whatsAppId}
				refreshSignal={previewNonce}
				onConfirmRequest={setConfirmRequest}
			/>
		);
	};

	return (
		<>
			<Dialog
				open={open}
				onClose={onClose}
				fullScreen={fullScreen}
				maxWidth={false}
				PaperProps={{ className: classes.paper, elevation: 0 }}
				BackdropProps={{ className: classes.backdrop }}
				aria-labelledby="admin-tools-title"
			>
				<div className={classes.header}>
					<span className={classes.headerIcon} aria-hidden="true">
						<ShieldAlert size={20} strokeWidth={1.8} />
					</span>
					<div style={{ minWidth: 0 }}>
						<div className={classes.eyebrow}>{t("eyebrow")}</div>
						<h2 id="admin-tools-title" className={classes.title}>
							{whatsApp.name}
						</h2>
						<div className={classes.subtitle}>
							{[subtitle, t(`provider.${whatsApp.provider || "waha"}`, { defaultValue: whatsApp.provider })]
								.filter(Boolean)
								.join(" · ")}
						</div>
					</div>
					<button type="button" className={classes.closeBtn} onClick={onClose} aria-label={t("close")}>
						<X size={18} strokeWidth={2} />
					</button>
				</div>

				<div className={classes.body}>
					<nav className={classes.nav} aria-label={t("eyebrow")}>
						{SECTIONS.map(({ id, icon: Icon, label }) => (
							<button
								key={id}
								type="button"
								className={`${classes.navItem} ${section === id ? classes.navItemActive : ""}`}
								onClick={() => setSection(id)}
								aria-current={section === id ? "page" : undefined}
							>
								<Icon size={16} strokeWidth={1.8} />
								<span>{t(label)}</span>
								{activeOp?.operation === id ? (
									<span className={classes.navBadge} title={t("statusLabel.running")} />
								) : null}
							</button>
						))}
						<div className={classes.navNote}>
							<ShieldAlert size={14} strokeWidth={1.8} />
							<span>{t("nav.note")}</span>
						</div>
					</nav>
					<div className={classes.content}>{renderContent()}</div>
				</div>
			</Dialog>

			<ConfirmDialog
				request={confirmRequest}
				onClose={() => setConfirmRequest(null)}
				onSubmit={handleConfirmSubmit}
			/>
		</>
	);
};

export default ConnectionAdminToolsModal;
